import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { DesignTestInputDto } from '../../../../models/job-request-iterations-dto.interface';
import { OverviewRowDto } from '../../../../models/job-request-overview-dto.interface';
import { CellDefinition, Worksheet } from '../../../../shared/components/spreadsheet/spreadsheet.models';
import { SpreadsheetService } from '../../../../shared/components/spreadsheet/spreadsheet.service';
import { evaluateCustomFormula } from '../../../../shared/utils/custom-test-formula';

interface CustomParameter {
  name: string;
  key?: string | null;
  unit?: string | null;
}

interface CustomOutputParameter extends CustomParameter {
  formula?: string | null;
}

interface CustomTableColumn extends CustomOutputParameter {
  kind: 'input' | 'output';
}

interface CustomTableDefinition {
  name: string;
  key?: string | null;
  rowCount: number;
  columns: CustomTableColumn[];
}

interface CustomDefinition {
  inputParameters: CustomParameter[];
  outputParameters: CustomOutputParameter[];
  table?: CustomTableDefinition | null;
  spreadsheet?: Worksheet | null;
}

interface SpreadsheetCellView {
  address: string;
  type: CellDefinition['type'] | null;
  value: string;
}

@Component({
  selector: 'app-others-vertical',
  standalone: false,
  templateUrl: './others-vertical.component.html',
  styleUrl: '../../job-request-column-report.component.scss'
})
export class OthersVerticalComponent implements OnChanges {
  @Input() othersTest: any;
  @Input() test?: DesignTestInputDto;
  @Input() customRow?: OverviewRowDto;

  protected definition: CustomDefinition | null = null;
  protected spreadsheetRows: SpreadsheetCellView[][] = [];
  private readonly spreadsheetService = inject(SpreadsheetService);
  private inputParameters: Record<string, string> = {};
  private outputParameters: Record<string, string> = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['test'] && !changes['customRow']) return;

    this.inputParameters = this.readParameters(this.test?.content);
    this.outputParameters = this.readParameters(this.test?.testOutputs[0]?.content);

    try {
      const definitionJson = this.test?.customTestDefinitionJson ?? this.customRow?.customTestDefinitionJson;
      this.definition = definitionJson
        ? JSON.parse(definitionJson) as CustomDefinition
        : null;
    } catch {
      this.definition = null;
    }

    this.spreadsheetRows = this.definition?.spreadsheet
      ? this.buildSpreadsheetRows(this.definition.spreadsheet)
      : [];
  }

  protected parameterKey(param: CustomParameter): string {
    return (param.key ?? '').trim() || param.name;
  }

  protected displayLabel(param: CustomParameter): string {
    return param.unit ? `${param.name} (${param.unit})` : param.name;
  }

  protected inputValue(key: string): string {
    return this.inputParameters[key] ?? this.customRow?.inputParameters?.[key] ?? '';
  }

  protected outputValue(param: CustomOutputParameter): string {
    if (!(param.formula ?? '').trim()) {
      const key = this.parameterKey(param);
      return this.outputParameters[key] ?? this.customRow?.outputParameters?.[key] ?? '';
    }

    const values: Record<string, string> = {};
    for (const input of this.definition?.inputParameters ?? []) {
      const key = this.parameterKey(input);
      values[key] = this.inputValue(key);
    }
    return evaluateCustomFormula(param.formula!, values);
  }

  protected tableRowIndices(rowCount: number): number[] {
    return Array.from({ length: Math.max(0, rowCount) }, (_, index) => index);
  }

  protected tableCellValue(table: CustomTableDefinition, rowIndex: number, column: CustomTableColumn): string {
    return this.tableCellValueInternal(table, rowIndex, column, new Set<string>());
  }

  private tableCellKey(table: CustomTableDefinition, rowIndex: number, column: CustomTableColumn): string {
    const tableKey = (table.key ?? '').trim() || table.name;
    return `${tableKey}__r${rowIndex + 1}__${this.parameterKey(column)}`;
  }

  private tableCellValueInternal(
    table: CustomTableDefinition,
    rowIndex: number,
    column: CustomTableColumn,
    visiting: Set<string>
  ): string {
    const key = this.tableCellKey(table, rowIndex, column);
    if (column.kind === 'input') return this.inputValue(key);

    const formula = (column.formula ?? '').trim();
    if (!formula) return this.outputParameters[key] ?? this.customRow?.outputParameters?.[key] ?? '';
    if (visiting.has(key)) return '';

    const nextVisiting = new Set(visiting);
    nextVisiting.add(key);
    const values: Record<string, string> = {};

    for (const tableColumn of table.columns) {
      const columnKey = this.parameterKey(tableColumn);
      if (tableColumn.kind === 'input') {
        values[columnKey] = this.inputValue(this.tableCellKey(table, rowIndex, tableColumn));
      }

      if (!formula.includes(`${columnKey}_sum`)) continue;

      let sum = 0;
      for (const index of this.tableRowIndices(table.rowCount)) {
        const cellValue = this.tableCellValueInternal(table, index, tableColumn, nextVisiting);
        const numericValue = parseFloat(cellValue);
        if (Number.isFinite(numericValue)) sum += numericValue;
      }
      values[`${columnKey}_sum`] = String(sum);
    }

    return evaluateCustomFormula(formula, values);
  }

  private readParameters(content?: string | null): Record<string, string> {
    if (!content) return {};

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return {};

    const values: Record<string, string> = {};
    for (const element of Array.from(document.getElementsByTagName('*'))) {
      if (element.children.length > 0) continue;
      const key = element.getAttribute('key') ?? element.localName;
      const value = element.textContent?.trim();
      if (key && value) values[key] = value;
    }
    return values;
  }

  private buildSpreadsheetRows(source: Worksheet): SpreadsheetCellView[][] {
    const cells: Record<string, CellDefinition> = {};
    for (const [address, cell] of Object.entries(source.cells ?? {})) {
      if (cell.type === 'number') {
        const value = this.inputValue(address);
        const numberValue = Number(value);
        cells[address] = { type: 'number', value: value !== '' && Number.isFinite(numberValue) ? numberValue : '' };
      } else {
        cells[address] = { ...cell };
      }
    }

    const worksheet: Worksheet = { ...source, cells };
    const calculation = this.spreadsheetService.recalculate(worksheet);
    const rows: SpreadsheetCellView[][] = [];
    for (let row = 0; row < worksheet.rows; row++) {
      const rowCells: SpreadsheetCellView[] = [];
      for (let column = 0; column < worksheet.columns; column++) {
        const address = this.spreadsheetService.indexesToCellAddress(row, column);
        const cell = cells[address];
        rowCells.push({
          address,
          type: cell?.type ?? null,
          value: cell?.type === 'formula'
            ? calculation.errors[address] ? '#ERROR!' : String(calculation.values[address] ?? '')
            : String(cell?.value ?? '')
        });
      }
      rows.push(rowCells);
    }
    return rows;
  }
}
